import { useEffect, useMemo, useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import useAxios from "@hooks/use-axios";
import { HOLDER_TYPE_LABEL, SUPERADMIN_ENDPOINTS } from "../misc/constants";
import { brandLoginsDefaults, brandLoginsSchema, toHolderPayload } from "../misc/schema";
import { newHolderExtIdPair } from "../misc/ids";
import { toRows } from "../misc/response";

// Step 3 of the flow: the brand list from step 2 is the source of both fields
// the console never asks for — `api_key` and `account_ext_id` — so this hook
// fetches that list and sends the brand's two logins in the one array call the
// endpoint takes. There is no holder list endpoint, so what was just created is
// kept on screen instead.
const useSuperadminHolders = () => {
    const { axiosInstance, loading } = useAxios({ base: "base", alertTypeOnSuccess: "toast" });
    const { axiosInstance: listInstance, loading: listLoading } = useAxios({ base: "base", initialLoader: true });

    const [brands, setBrands] = useState([]);
    const [created, setCreated] = useState([]);

    const methods = useForm({
        resolver     : yupResolver(brandLoginsSchema),
        defaultValues: brandLoginsDefaults
    });

    const brandId = methods.watch("account_id");

    const selectedBrand = useMemo(
        () => brands.find((brand) => brand._id === brandId) || null,
        [brands, brandId]
    );

    const brandOptions = useMemo(
        () => brands.map((brand) => ({ value: brand._id, label: brand.name || brand.ext_id, status: brand.status })),
        [brands]
    );

    const fetchBrands = async () => {
        try {
            const res = await listInstance.get(SUPERADMIN_ENDPOINTS.BRANDS);
            setBrands(toRows(res));
        } catch {
            setBrands([]);
        }
    };

    // Both fields open prefilled and stay editable.
    const resetForm = (accountId = "") => {
        const extIds = newHolderExtIdPair();
        methods.reset({
            ...brandLoginsDefaults,
            account_id      : accountId,
            admin_ext_id    : extIds.admin,
            affiliate_ext_id: extIds.affiliate
        });
    };

    useEffect(() => {
        fetchBrands();
        resetForm();
    }, []);

    const submit = methods.handleSubmit(async (values) => {
        const brand = brands.find((row) => row._id === values.account_id);
        if (!brand) return;

        const payload = toHolderPayload(values, brand);
        // Both sides switched off — the endpoint would accept the empty array
        // and do nothing, so there is nothing to send.
        if (!payload.length) return;

        await axiosInstance.post(SUPERADMIN_ENDPOINTS.HOLDERS, payload);

        // Passwords are not readable back from anywhere, so the batch that just
        // went out is listed with its credentials until the page is left.
        setCreated((current) => [
            ...payload.map((holder) => ({
                id      : holder.holder_ext_id,
                meta    : `${HOLDER_TYPE_LABEL[holder.holder_type] || holder.holder_type} · ${brand.name || brand.ext_id}`,
                name    : holder.holder_name,
                email   : holder.email,
                password: holder.password
            })),
            ...current
        ]);
        resetForm(values.account_id);
    });

    const dismissCreated = (id) => setCreated((current) => current.filter((row) => row.id !== id));

    return {
        brandOptions,
        selectedBrand,
        loading         : loading || listLoading,
        methods,
        submit,
        created,
        dismissCreated,
        regenerateExtIds: () => {
            const extIds = newHolderExtIdPair();
            methods.setValue("admin_ext_id", extIds.admin, { shouldValidate: true });
            methods.setValue("affiliate_ext_id", extIds.affiliate, { shouldValidate: true });
        },
        refreshBrands: fetchBrands
    };
};

export default useSuperadminHolders;
